import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { formatDateTime } from '@/lib/dates';
import { ACCOUNT_SERIES, ACCOUNT_SERIES_KEYS, seriesBadgeClass } from '@/lib/accountSeries';
import {
  Loader2, RefreshCw, ChevronLeft, ChevronRight, ArrowUp, ArrowDown,
  FileSpreadsheet, Filter, Columns3, CheckCircle2, XCircle, MinusCircle,
  ChevronDown, Search, Database, PanelRightOpen, PanelRightClose, MapPin, Eye, Hash,
  AlertTriangle, Shield, Download, History, Zap, Circle, Clock,
  CalendarDays, BarChart3, TrendingUp, ListChecks, Globe, Receipt,
  EyeOff, Users, Pencil, ArrowUpDown,
} from 'lucide-react';




import TownClassifier from './export-builder/TownClassifier';




import TemplateBuilder from './export-builder/TemplateBuilder';
import AddressAliasManager from './export-builder/AddressAliasManager';
import OutputPreview from './export-builder/OutputPreview';
import InvoiceRunSettings from './export-builder/InvoiceRunSettings';
import AnomalyPanel from './export-builder/AnomalyPanel';
import ExportPanel from './export-builder/ExportPanel';
import ExportHistory from './export-builder/ExportHistory';
import InvoicedJobsReport from './export-builder/InvoicedJobsReport';
import JobEditModal from './export-builder/JobEditModal';
import SortDialog from './export-builder/SortDialog';
import GridSortDialog from './export-builder/GridSortDialog';
import type { GridSortSpec } from './export-builder/GridSortDialog';




import { runAnomalyDetection, mergeReviewStatuses } from './export-builder/anomalyEngine';
import { deriveInvoicePassengerName } from './export-builder/transforms';
import {
  DEFAULT_ANOMALY_CONFIG,
} from './export-builder/types';
import type {
  ExportTemplateColumn, AddressAliasRule, MarginRule,
  InvoiceAssignment, InvoiceRunSummary, InvoiceRunSettings as IInvoiceRunSettings,
  AnomalyConfig, RowAnomalyResult, AnomalySummary, AnomalyType,
  ExportTemplate, ExportRun, TownClassification, AccountScope,
  SortSpec,
} from './export-builder/types';





// ─── Previous Invoice Info Type ─────────────────────────────────────
interface PreviousInvoiceInfo {
  invoice_number: number;
  template_name: string | null;
  target_system: string | null;
  invoiced_at: string;
  export_run_id: string | null;
}


// ─── Types ──────────────────────────────────────────────────────────
interface ExportJobRow {
  tc_id: string; job_id: string; project: string | null; account_id: string | null;
  reference: string | null; passenger_first_name: string | null; passenger_last_name: string | null;
  passenger_name: string | null; pick_up_location: string | null; drop_off_location: string | null;
  pu_date: string | null; pu_time: string | null; do_time: string | null;
  tc_vehicle_number: string | null; tc_driver_name: string | null;
  grand_total: number | null; direct_settled: number | null; tc_cost_code: string | null;
  stage: string | null; fleet: string | null; trip_total: number | null;
  trip_base_fare: number | null; job_km: number | null;
  dcp_id: string | null; paid_amount: number | null; cash_amt: number | null;
  eftpos_amt: number | null; totalmobility_amt: number | null; tm_hoist_amt: number | null;
  account_amt: number | null; other_amt: number | null; discount: number | null;
  eftpos_valid: boolean | null; total_mobility_valid: boolean | null; tm_hoist_valid: boolean | null;
  reconciled: boolean | null; payment_notes: string | null; ecard_amt: number | null;
  ecard_valid: boolean | null; driver_paid: boolean | null;
  account_name: string | null; account_type: string | null; account_series_type: string | null;
  billed_status: string | null; billing_name: string | null; invoice_email: string | null;
  accounts_payable_email: string | null; payment_terms: string | null; account_category: string | null;
  crm_default_project: string | null; crm_default_cost_code: string | null;
  crm_default_reference: string | null; crm_is_active: boolean | null;
  account_series: string | null; net_after_tm: number | null;
  missing_crm_account: boolean; missing_payment_row: boolean;
  missing_project: boolean; missing_reference: boolean; missing_cost_code: boolean;
}

type ValidationStatus = 'fully_validated' | 'partially_validated' | 'not_validated' | 'no_payment';

interface ColDef {
  key: string; label: string; group: string; width?: number;
  align?: 'left' | 'center' | 'right'; defaultVisible?: boolean;
}

type ReadinessState = 'not_ready' | 'needs_review' | 'ready';

// ─── Helpers ────────────────────────────────────────────────────────
function getCurrentMonthRange() {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const last = new Date(y, m + 1, 0).getDate();
  const to = `${y}-${String(m + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { from, to };
}

function getLastMonthRange() {
  const now = new Date();
  const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const m = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
  const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const last = new Date(y, m + 1, 0).getDate();
  const to = `${y}-${String(m + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { from, to };
}

function getValidationStatus(row: ExportJobRow): ValidationStatus {
  if (!row.dcp_id) return 'no_payment';
  const checks: boolean[] = [];
  if (Number(row.eftpos_amt || 0) > 0) checks.push(!!row.eftpos_valid);
  if (Number(row.totalmobility_amt || 0) > 0) checks.push(!!row.total_mobility_valid);
  if (Number(row.tm_hoist_amt || 0) > 0) checks.push(!!row.tm_hoist_valid);
  checks.push(!!row.reconciled);
  if (checks.length === 0) return 'not_validated';
  if (checks.every(Boolean)) return 'fully_validated';
  if (checks.some(Boolean)) return 'partially_validated';
  return 'not_validated';
}

const fmt = (v: number | null) => v == null ? '-' : `$${Number(v).toFixed(2)}`;
const fmtTime = (t: string | null) => t ? t.substring(0, 5) : '-';

// ─── Column Definitions ─────────────────────────────────────────────
const ALL_COLUMNS: ColDef[] = [
  { key: 'job_id', label: 'Job ID', group: 'TaxiCaller', width: 100, defaultVisible: true },
  { key: 'project', label: 'Project', group: 'TaxiCaller', width: 160, defaultVisible: true },
  { key: 'account_id', label: 'Account ID', group: 'TaxiCaller', width: 90, defaultVisible: true },
  { key: 'reference', label: 'Reference', group: 'TaxiCaller', width: 140, defaultVisible: true },
  { key: 'passenger_name', label: 'Passenger', group: 'TaxiCaller', width: 150, defaultVisible: true },
  { key: 'pick_up_location', label: 'Pickup', group: 'TaxiCaller', width: 200, defaultVisible: true },
  { key: 'drop_off_location', label: 'Dropoff', group: 'TaxiCaller', width: 200, defaultVisible: true },
  { key: 'pu_date', label: 'Date', group: 'TaxiCaller', width: 95, defaultVisible: true },
  { key: 'pu_time', label: 'PU Time', group: 'TaxiCaller', width: 70, defaultVisible: true },
  { key: 'do_time', label: 'DO Time', group: 'TaxiCaller', width: 70, defaultVisible: true },
  { key: 'tc_vehicle_number', label: 'Vehicle', group: 'TaxiCaller', width: 70, defaultVisible: true },
  { key: 'tc_driver_name', label: 'Driver', group: 'TaxiCaller', width: 140, defaultVisible: true },
  { key: 'grand_total', label: 'Grand Total', group: 'TaxiCaller', width: 95, align: 'right', defaultVisible: true },
  { key: 'direct_settled', label: 'Direct Settled', group: 'TaxiCaller', width: 100, align: 'right', defaultVisible: false },
  { key: 'tc_cost_code', label: 'Cost Code', group: 'TaxiCaller', width: 100, defaultVisible: true },
  { key: 'stage', label: 'Stage', group: 'TaxiCaller', width: 100, defaultVisible: true },
  { key: 'fleet', label: 'Fleet', group: 'TaxiCaller', width: 70, defaultVisible: true },
  { key: 'paid_amount', label: 'Paid Amt', group: 'Payment', width: 85, align: 'right', defaultVisible: true },
  { key: 'cash_amt', label: 'Cash', group: 'Payment', width: 75, align: 'right', defaultVisible: true },
  { key: 'eftpos_amt', label: 'EFTPOS', group: 'Payment', width: 80, align: 'right', defaultVisible: true },
  { key: 'totalmobility_amt', label: 'TM Amt', group: 'Payment', width: 80, align: 'right', defaultVisible: true },
  { key: 'tm_hoist_amt', label: 'Hoist', group: 'Payment', width: 75, align: 'right', defaultVisible: true },
  { key: 'account_amt', label: 'Account', group: 'Payment', width: 80, align: 'right', defaultVisible: true },
  { key: 'other_amt', label: 'Other', group: 'Payment', width: 75, align: 'right', defaultVisible: false },
  { key: 'discount', label: 'Discount', group: 'Payment', width: 80, align: 'right', defaultVisible: false },
  { key: 'payment_notes', label: 'Notes', group: 'Payment', width: 160, defaultVisible: false },
  { key: 'account_name', label: 'Account Name', group: 'CRM', width: 180, defaultVisible: true },
  { key: 'account_series_type', label: 'Series Type', group: 'CRM', width: 100, defaultVisible: false },
  { key: 'billed_status', label: 'Billed', group: 'CRM', width: 85, defaultVisible: true },
  { key: 'billing_name', label: 'Billing Name', group: 'CRM', width: 150, defaultVisible: false },
  { key: 'invoice_email', label: 'Invoice Email', group: 'CRM', width: 180, defaultVisible: false },
  { key: 'payment_terms', label: 'Payment Terms', group: 'CRM', width: 110, defaultVisible: false },
  { key: 'account_series', label: 'Series', group: 'Derived', width: 60, align: 'center', defaultVisible: true },
  { key: 'net_after_tm', label: 'Net After TM', group: 'Derived', width: 100, align: 'right', defaultVisible: true },
  { key: 'validation_status', label: 'Validation', group: 'Derived', width: 120, align: 'center', defaultVisible: true },
  { key: 'missing_data', label: 'Warnings', group: 'Derived', width: 130, defaultVisible: true },
];

const FETCH_BATCH_SIZE = 1000; // Supabase max rows per request


// ─── Main Component ─────────────────────────────────────────────────
const ExportBuilder: React.FC = () => {
  const range = getCurrentMonthRange();
  const [dateFrom, setDateFrom] = useState(range.from);
  const [dateTo, setDateTo] = useState(range.to);
  const [seriesFilter, setSeriesFilter] = useState<string[]>(ACCOUNT_SERIES_KEYS);
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [billedFilter, setBilledFilter] = useState<'all' | 'billed' | 'non-billed'>('billed');

  const [validatedOnly, setValidatedOnly] = useState(false);
  const [stageFilter, setStageFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [fleetFilter, setFleetFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');

  // ─── Account Picker Filter State ────────────────────────────────
  const [filterAccountIds, setFilterAccountIds] = useState<string[]>([]);
  const [showAccountPicker, setShowAccountPicker] = useState(false);
  const [accountFilterSeries, setAccountFilterSeries] = useState<string[]>(ACCOUNT_SERIES_KEYS);



  const [rows, setRows] = useState<ExportJobRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setPage = (_n: number) => {}; // No-op — pagination removed, all rows loaded at once

  const [gridSortSpecs, setGridSortSpecs] = useState<GridSortSpec[]>([
    { field: 'account_id', direction: 'asc' },
  ]);
  const [gridSortDialogOpen, setGridSortDialogOpen] = useState(false);


  const [visibleCols, setVisibleCols] = useState<Set<string>>(
    new Set(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key))
  );
  const [colOrder, setColOrder] = useState<string[]>(ALL_COLUMNS.map(c => c.key));
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templateColumns, setTemplateColumns] = useState<ExportTemplateColumn[]>([]);

  const [aliasRules, setAliasRules] = useState<AddressAliasRule[]>([]);
  const [aliasManagerOpen, setAliasManagerOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [marginRules, setMarginRules] = useState<MarginRule[]>([]);
  const [invoiceAssignments, setInvoiceAssignments] = useState<Map<string, InvoiceAssignment> | null>(null);
  const [invoiceSummary, setInvoiceSummary] = useState<InvoiceRunSummary | null>(null);
  const [invoiceSettings, setInvoiceSettings] = useState<IInvoiceRunSettings | null>(null);

  const [anomalyConfig, setAnomalyConfig] = useState<AnomalyConfig>(DEFAULT_ANOMALY_CONFIG);
  const [anomalyResults, setAnomalyResults] = useState<Map<string, RowAnomalyResult> | null>(null);
  const [anomalySummary, setAnomalySummary] = useState<AnomalySummary | null>(null);
  const [anomalyRunning, setAnomalyRunning] = useState(false);
  const [showAnomalyPanel, setShowAnomalyPanel] = useState(false);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

  // ─── Account Scope State ─────────────────────────────────────────
  const [accountScope, setAccountScope] = useState<AccountScope | null>(null);

  // ─── Export Panel / History / Template State ────────────────────
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [showExportHistory, setShowExportHistory] = useState(false);
  const [showInvoicedJobsReport, setShowInvoicedJobsReport] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ExportTemplate | null>(null);

  // ─── Template Default Sort State ────────────────────────────────
  const [templateDefaultSort, setTemplateDefaultSort] = useState<SortSpec[] | null>(null);

  // ─── Preview Sort State (lifted from OutputPreview) ─────────────
  const [previewSortSpecs, setPreviewSortSpecs] = useState<SortSpec[]>([]);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const appliedDefaultSortRef = useRef<string | null>(null);


  // ─── Town Classifier State ──────────────────────────────────────
  const [townClassifications, setTownClassifications] = useState<TownClassification[]>([]);
  const [townClassifierOpen, setTownClassifierOpen] = useState(false);

  // ─── Job Edit Modal State ───────────────────────────────────────
  const [editingRow, setEditingRow] = useState<ExportJobRow | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // ─── Previous Invoice Records State ─────────────────────────────
  const [previousInvoices, setPreviousInvoices] = useState<Map<string, PreviousInvoiceInfo>>(new Map());
  const [loadingPreviousInvoices, setLoadingPreviousInvoices] = useState(false);

  const dragCol = useRef<string | null>(null);



  // ─── Load Account Scope + Default Sort from Template ────────────
  useEffect(() => {
    if (selectedTemplateId) {
      supabase.from('export_templates').select('account_scope, default_sort').eq('id', selectedTemplateId).single().then(({ data }) => {
        if (data?.account_scope) {
          setAccountScope(data.account_scope as AccountScope);
        } else {
          setAccountScope(null);
        }
        if (data?.default_sort && Array.isArray(data.default_sort)) {
          setTemplateDefaultSort(data.default_sort as SortSpec[]);
        } else {
          setTemplateDefaultSort(null);
        }
      });
    } else {
      setAccountScope(null);
      setTemplateDefaultSort(null);
    }
  }, [selectedTemplateId]);

  // ─── Auto-apply default sort from template when loaded ──────────
  useEffect(() => {
    const key = selectedTemplateId || '__none__';
    if (appliedDefaultSortRef.current !== key) {
      appliedDefaultSortRef.current = key;
      if (templateDefaultSort && templateDefaultSort.length > 0) {
        setPreviewSortSpecs(templateDefaultSort);
      } else {
        setPreviewSortSpecs([]);
      }
    }
  }, [selectedTemplateId, templateDefaultSort]);


  // ─── Save Sort to Template Handler ──────────────────────────────
  const handleSaveSortToTemplate = useCallback(async (sortSpecs: SortSpec[]) => {
    if (!selectedTemplateId) return;
    const { error } = await supabase.from('export_templates')
      .update({ default_sort: sortSpecs.length > 0 ? sortSpecs : null, updated_at: new Date().toISOString(), updated_by: 'admin' })
      .eq('id', selectedTemplateId);
    if (error) {
      toast({ title: 'Error', description: 'Failed to save sort configuration to template', variant: 'destructive' });
      throw error;
    }
    setTemplateDefaultSort(sortSpecs.length > 0 ? sortSpecs : null);
    toast({ title: 'Sort Saved', description: `Sort configuration saved to template` });
  }, [selectedTemplateId]);




  // ─── Load Town Classifications ──────────────────────────────────
  const loadTownClassifications = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('address_town_classifications')
        .select('*')
        .order('location_name');
      if (error) throw error;
      setTownClassifications((data || []) as TownClassification[]);
    } catch (err: any) {
      console.error('Error loading town classifications:', err);
    }
  }, []);

  useEffect(() => { loadTownClassifications(); }, [loadTownClassifications]);

  // ─── Build Town Classifications Map (for transforms) ────────────
  const townClassificationsMap = useMemo(() => {
    const map = new Map<string, TownClassification>();
    for (const c of townClassifications) {
      map.set(c.location_name.toLowerCase(), c);
    }
    return map;
  }, [townClassifications]);



  // ─── Load Alias Rules ───────────────────────────────────────────
  const loadAliasRules = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('address_alias_rules')
        .select('*')
        .order('rule_name');
      if (error) throw error;
      setAliasRules((data || []) as AddressAliasRule[]);
    } catch (err: any) {
      console.error('Error loading alias rules:', err);
      toast({ title: 'Error Loading Alias Rules', description: err.message || 'Failed to load address alias rules. They will not be applied to transforms.', variant: 'destructive' });
    }
  }, []);

  useEffect(() => { loadAliasRules(); }, [loadAliasRules]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadingProgress('');
    try {
      // Helper to apply all filters and sorts to a query
      const applyFiltersAndSort = (q: any) => {
        if (dateFrom) q = q.gte('pu_date', dateFrom);
        if (dateTo) q = q.lte('pu_date', dateTo);
        if (seriesFilter.length > 0 && seriesFilter.length < ACCOUNT_SERIES_KEYS.length) {
          q = q.in('account_series', seriesFilter);
        }
        if (accountNumber.trim()) q = q.ilike('account_id', `%${accountNumber.trim()}%`);
        if (filterAccountIds.length > 0) q = q.in('account_id', filterAccountIds);
        if (accountName.trim()) q = q.ilike('account_name', `%${accountName.trim()}%`);

        if (billedFilter === 'billed') q = q.eq('billed_status', 'Billed');
        if (billedFilter === 'non-billed') q = q.or('billed_status.neq.Billed,billed_status.is.null');
        if (stageFilter) q = q.eq('stage', stageFilter);
        if (projectFilter.trim()) q = q.ilike('project', `%${projectFilter.trim()}%`);
        if (fleetFilter) q = q.eq('fleet', fleetFilter);
        if (driverFilter.trim()) q = q.ilike('tc_driver_name', `%${driverFilter.trim()}%`);
        if (vehicleFilter.trim()) q = q.ilike('tc_vehicle_number', `%${vehicleFilter.trim()}%`);

        // Multi-column server-side sort
        const UNSORTABLE = new Set(['validation_status', 'missing_data']);
        const usedSortFields = new Set<string>();
        for (const spec of gridSortSpecs) {
          const field = UNSORTABLE.has(spec.field) ? 'pu_date' : spec.field;
          if (!usedSortFields.has(field)) {
            q = q.order(field, { ascending: spec.direction === 'asc' });
            usedSortFields.add(field);
          }
        }
        const tiebreakers = [
          { field: 'account_series', ascending: true },
          { field: 'account_id', ascending: true },
          { field: 'pu_date', ascending: true },
        ];
        for (const tb of tiebreakers) {
          if (!usedSortFields.has(tb.field)) {
            q = q.order(tb.field, { ascending: tb.ascending });
            usedSortFields.add(tb.field);
          }
        }
        return q;
      };

      // First batch: fetch with count to know total
      let firstQuery = supabase.from('export_builder_jobs').select('*', { count: 'exact' });
      firstQuery = applyFiltersAndSort(firstQuery);
      firstQuery = firstQuery.range(0, FETCH_BATCH_SIZE - 1);

      const { data: firstBatch, count, error: firstError } = await firstQuery;
      if (firstError) throw firstError;

      const total = count || 0;
      setTotalCount(total);

      const allRows: ExportJobRow[] = [...((firstBatch || []) as ExportJobRow[])];

      // Fetch remaining batches if more data exists
      if (total > FETCH_BATCH_SIZE) {
        const totalBatches = Math.ceil(total / FETCH_BATCH_SIZE);
        setLoadingProgress(`Loading batch 1 of ${totalBatches}...`);

        for (let offset = FETCH_BATCH_SIZE; offset < total; offset += FETCH_BATCH_SIZE) {
          const batchNum = Math.floor(offset / FETCH_BATCH_SIZE) + 1;
          setLoadingProgress(`Loading batch ${batchNum} of ${totalBatches} (${allRows.length.toLocaleString()} rows so far)...`);

          let batchQuery = supabase.from('export_builder_jobs').select('*');
          batchQuery = applyFiltersAndSort(batchQuery);
          batchQuery = batchQuery.range(offset, offset + FETCH_BATCH_SIZE - 1);

          const { data: batchData, error: batchError } = await batchQuery;
          if (batchError) throw batchError;
          if (batchData) allRows.push(...(batchData as ExportJobRow[]));
        }
      }

      setLoadingProgress('');
      setRows(allRows);
      setInvoiceAssignments(null);
      setInvoiceSummary(null);

      if (total > FETCH_BATCH_SIZE) {
        toast({
          title: 'All Data Loaded',
          description: `${allRows.length.toLocaleString()} rows loaded across ${Math.ceil(total / FETCH_BATCH_SIZE)} batches`,
        });
      }
    } catch (err: any) {
      console.error('Export Builder query error:', err);
      toast({ title: 'Data Load Error', description: err.message || 'Failed to load data. Check your connection and try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
      setLoadingProgress('');
    }
  }, [dateFrom, dateTo, seriesFilter, accountNumber, accountName, billedFilter, validatedOnly, stageFilter, projectFilter, fleetFilter, driverFilter, vehicleFilter, filterAccountIds, gridSortSpecs]);




  useEffect(() => { loadData(); }, [loadData]);

  // ─── Load Previous Invoice Records ──────────────────────────────
  // Matches on both job_id (stable TaxiCaller identifier) and tc_id
  // (internal — can change on re-import). Preferring job_id ensures
  // historically-invoiced jobs still show even after a TaxiCaller
  // re-import that changed their internal tc_id.
  const loadPreviousInvoices = useCallback(async (currentRows: ExportJobRow[]) => {
    if (currentRows.length === 0) {
      setPreviousInvoices(new Map());
      return;
    }
    setLoadingPreviousInvoices(true);
    try {
      const BATCH_SIZE = 500;
      const jobIds = Array.from(new Set(currentRows.map(r => r.job_id).filter(Boolean)));
      const tcIds = Array.from(new Set(currentRows.map(r => r.tc_id).filter(Boolean)));
      const allResults: any[] = [];

      // Query 1: Match on job_id (primary — stable TaxiCaller ID)
      for (let i = 0; i < jobIds.length; i += BATCH_SIZE) {
        const batch = jobIds.slice(i, i + BATCH_SIZE);
        const { data, error } = await supabase
          .from('job_invoice_records')
          .select('tc_id, job_id, invoice_number, template_name, target_system, invoiced_at, export_run_id')
          .in('job_id', batch);
        if (error) throw error;
        if (data) allResults.push(...data);
      }

      // Query 2: Match on tc_id (fallback — covers records where job_id may be null)
      for (let i = 0; i < tcIds.length; i += BATCH_SIZE) {
        const batch = tcIds.slice(i, i + BATCH_SIZE);
        const { data, error } = await supabase
          .from('job_invoice_records')
          .select('tc_id, job_id, invoice_number, template_name, target_system, invoiced_at, export_run_id')
          .in('tc_id', batch);
        if (error) throw error;
        if (data) allResults.push(...data);
      }

      // Build lookups by job_id and tc_id — keep the latest per identifier
      const byJobId = new Map<string, any>();
      const byTcId = new Map<string, any>();
      for (const r of allResults) {
        if (r.job_id) {
          const existing = byJobId.get(r.job_id);
          if (!existing || new Date(r.invoiced_at) > new Date(existing.invoiced_at)) {
            byJobId.set(r.job_id, r);
          }
        }
        if (r.tc_id) {
          const existing = byTcId.get(r.tc_id);
          if (!existing || new Date(r.invoiced_at) > new Date(existing.invoiced_at)) {
            byTcId.set(r.tc_id, r);
          }
        }
      }

      // Build final map keyed by the row's tc_id (what the grid uses for rendering),
      // preferring the job_id match over the tc_id match.
      const map = new Map<string, PreviousInvoiceInfo>();
      for (const row of currentRows) {
        const match = (row.job_id && byJobId.get(row.job_id)) || (row.tc_id && byTcId.get(row.tc_id));
        if (match) {
          map.set(row.tc_id, {
            invoice_number: match.invoice_number,
            template_name: match.template_name,
            target_system: match.target_system,
            invoiced_at: match.invoiced_at,
            export_run_id: match.export_run_id,
          });
        }
      }
      setPreviousInvoices(map);
    } catch (err: any) {
      console.error('Failed to load previous invoices:', err);
      setPreviousInvoices(new Map());
    } finally {
      setLoadingPreviousInvoices(false);
    }
  }, []);

  // Load previous invoices whenever rows change
  useEffect(() => {
    loadPreviousInvoices(rows);
  }, [rows, loadPreviousInvoices]);




  const displayRows = useMemo(() => {
    let result = rows;
    if (validatedOnly) {
      result = result.filter(r => getValidationStatus(r) === 'fully_validated');
    }
    // Apply account scope filter (client-side) — from AccountScopePicker on template
    if (accountScope?.mode === 'selected' && accountScope.selectedAccountIds.length > 0) {
      const selectedSet = new Set(accountScope.selectedAccountIds);
      result = result.filter(r => r.account_id && selectedSet.has(r.account_id));
    }
    // NOTE: Per-column account filters are NOT applied here.
    // They only affect the preview and export output (see exportFilteredRows below).
    return result;
  }, [rows, validatedOnly, accountScope]);

  // ─── Export/Preview Filtered Rows ─────────────────────────────────
  // Per-column account filter from template columns — only affects
  // the Template Output Preview and the Export Panel, NOT the main grid.
  const exportFilteredRows = useMemo(() => {
    let result = displayRows;
    const accountIdCol = templateColumns.find(
      c => c.source_type === 'source_field' && c.source_field === 'account_id'
        && c.transform_config?.selectedAccountIds?.length > 0
    );
    if (accountIdCol) {
      const colFilterIds = new Set<string>(accountIdCol.transform_config.selectedAccountIds);
      result = result.filter(r => r.account_id && colFilterIds.has(r.account_id));
    }
    return result;
  }, [displayRows, templateColumns]);


  // ─── Part 16: Preview Invoice Assignments (Merged) ────────────────
  // When the user has a column bound to `assigned_invoice_number` (or other
  // invoice fields) BUT hasn't run a fresh Invoice Run, jobs that were
  // previously exported should still show their saved invoice number in the
  // preview/export. We build synthetic InvoiceAssignment records from the
  // `previousInvoices` map so `augmentRowWithInvoiceData` populates the
  // `assigned_invoice_number` field for those rows.
  //
  // Merge order:
  //   1. Start with fresh `invoiceAssignments` (from Invoice Run Settings — wins on conflicts)
  //   2. Fill in historical `previousInvoices` entries for any tc_id not already assigned
  //
  // This is passed to BOTH OutputPreview and ExportPanel so re-exports and
  // previously-invoiced jobs display their invoice numbers consistently.
  const previewInvoiceAssignments = useMemo((): Map<string, InvoiceAssignment> | null => {
    const hasFresh = invoiceAssignments && invoiceAssignments.size > 0;
    const hasHistorical = previousInvoices.size > 0;
    if (!hasFresh && !hasHistorical) return null;

    const merged = new Map<string, InvoiceAssignment>(invoiceAssignments ? invoiceAssignments : []);

    // Build lookup from exportFilteredRows for passenger name derivation
    const rowLookup = new Map<string, ExportJobRow>();
    for (const r of exportFilteredRows) {
      rowLookup.set(r.tc_id, r);
    }

    // Add synthetic assignments from previousInvoices for any row not already assigned
    for (const [tcId, info] of previousInvoices) {
      if (merged.has(tcId)) continue; // fresh assignment wins
      const row = rowLookup.get(tcId);
      const passengerName = row ? deriveInvoicePassengerName(row as any) : '';
      merged.set(tcId, {
        rowId: tcId,
        invoicePassengerName: passengerName,
        invoiceGroupKey: `prev::${tcId}`,
        assignedInvoiceNumber: info.invoice_number,
      });
    }

    return merged.size > 0 ? merged : null;
  }, [invoiceAssignments, previousInvoices, exportFilteredRows]);

  // ─── Part 16: Preview Invoice Summary (Merged) ────────────────────
  // If a fresh invoice run is active use that summary; otherwise build a
  // lightweight synthetic summary from previousInvoices so the preview
  // shows the historical invoice range.
  const previewInvoiceSummary = useMemo((): InvoiceRunSummary | null => {
    if (invoiceSummary) return invoiceSummary;
    if (!previewInvoiceAssignments || previewInvoiceAssignments.size === 0) return null;
    const numbers: number[] = [];
    const groupKeys = new Set<string>();
    for (const [, a] of previewInvoiceAssignments) {
      numbers.push(a.assignedInvoiceNumber);
      groupKeys.add(a.invoiceGroupKey);
    }
    if (numbers.length === 0) return null;
    return {
      startingNumber: Math.min(...numbers),
      endingNumber: Math.max(...numbers),
      uniqueGroups: groupKeys.size,
      totalRows: previewInvoiceAssignments.size,
      groupBreakdown: [],
    };
  }, [invoiceSummary, previewInvoiceAssignments]);



  // ─── Memoized Summary Stats ─────────────────────────────────────
  const stats = useMemo(() => {
    let gt = 0, tm = 0, ho = 0, net = 0, acc = 0, ca = 0, ef = 0;
    let fv = 0, pv = 0, nv = 0, np = 0;
    for (const r of displayRows) {
      gt += Number(r.grand_total || 0);
      tm += Number(r.totalmobility_amt || 0);
      ho += Number(r.tm_hoist_amt || 0);
      net += Number(r.net_after_tm || 0);
      acc += Number(r.account_amt || 0);
      ca += Number(r.cash_amt || 0);
      ef += Number(r.eftpos_amt || 0);
      const vs = getValidationStatus(r);
      if (vs === 'fully_validated') fv++;
      else if (vs === 'partially_validated') pv++;
      else if (vs === 'not_validated') nv++;
      else np++;
    }
    return { gt, tm, ho, net, acc, ca, ef, fv, pv, nv, np, count: displayRows.length };
  }, [displayRows]);

  // ─── Export Readiness ───────────────────────────────────────────
  const readiness = useMemo((): { state: ReadinessState; label: string; reasons: string[] } => {
    const reasons: string[] = [];

    if (!selectedTemplateId) {
      reasons.push('No template selected');
      return { state: 'not_ready', label: 'Not Ready', reasons };
    }

    const enabledCols = templateColumns.filter(c => c.is_enabled);
    if (enabledCols.length === 0) {
      reasons.push('No enabled export columns');
      return { state: 'not_ready', label: 'Not Ready', reasons };
    }

    if (displayRows.length === 0) {
      reasons.push('No data rows loaded');
      return { state: 'not_ready', label: 'Not Ready', reasons };
    }

    // Check for invoice fields without assignments
    const invoiceFields = ['assigned_invoice_number', 'invoice_group_key', 'invoice_passenger_name'];
    const hasInvoiceCols = enabledCols.some(c =>
      c.source_type === 'source_field' && c.source_field && invoiceFields.includes(c.source_field)
    );
    if (hasInvoiceCols && (!invoiceAssignments || invoiceAssignments.size === 0)) {
      reasons.push('Invoice columns used but not assigned');
    }

    // Check for unreviewed anomalies
    if (anomalySummary && anomalySummary.unreviewed > 0) {
      reasons.push(`${anomalySummary.unreviewed} unreviewed anomalies`);
    }

    // Check for required columns
    const requiredCols = enabledCols.filter(c => c.is_required);
    const blankRequired = requiredCols.filter(c => {
      if (c.source_type === 'blank') return true;
      if (c.source_type === 'source_field' && !c.source_field) return true;
      return false;
    });
    if (blankRequired.length > 0) {
      reasons.push(`${blankRequired.length} required column(s) may be blank`);
    }

    if (reasons.length > 0) {
      return { state: 'needs_review', label: 'Needs Review', reasons };
    }

    return { state: 'ready', label: 'Ready to Export', reasons: [] };
  }, [selectedTemplateId, templateColumns, displayRows, invoiceAssignments, anomalySummary]);

  // ─── Filters Snapshot for Export Run ────────────────────────────
  const filtersSnapshot = useMemo(() => ({
    dateFrom, dateTo, seriesFilter, accountNumber, accountName,
    billedFilter, validatedOnly, stageFilter, projectFilter,
    fleetFilter, driverFilter, vehicleFilter, filterAccountIds, accountFilterSeries,
  }), [dateFrom, dateTo, seriesFilter, accountNumber, accountName, billedFilter, validatedOnly, stageFilter, projectFilter, fleetFilter, driverFilter, vehicleFilter, filterAccountIds, accountFilterSeries]);


  // ─── Workflow Step Status ───────────────────────────────────────
  const workflowSteps = useMemo(() => {
    const hasData = displayRows.length > 0;
    const hasTemplate = !!selectedTemplateId;
    const hasEnabledCols = templateColumns.filter(c => c.is_enabled).length > 0;
    const hasInvoices = invoiceAssignments && invoiceAssignments.size > 0;
    const hasAnomalyScan = anomalyResults !== null;

    return [
      { label: 'Data', done: hasData, active: !hasData },
      { label: 'Template', done: hasTemplate && hasEnabledCols, active: hasData && !hasTemplate },
      { label: 'Preview', done: hasTemplate && hasEnabledCols && showPreview, active: hasTemplate && hasEnabledCols && !showPreview },
      { label: 'Invoices', done: !!hasInvoices, active: hasTemplate && hasEnabledCols && !hasInvoices, optional: true },
      { label: 'Exceptions', done: hasAnomalyScan && (anomalySummary?.unreviewed === 0), active: hasData && !hasAnomalyScan, optional: true },
      { label: 'Export', done: false, active: readiness.state === 'ready' },
    ];
  }, [displayRows, selectedTemplateId, templateColumns, showPreview, invoiceAssignments, anomalyResults, anomalySummary, readiness]);

  // ─── Grid Sort handler (column header click) ────────────────────
  const handleSort = useCallback((key: string) => {
    setGridSortSpecs(prev => {
      // If the first sort spec matches this key, toggle direction or remove
      if (prev.length > 0 && prev[0].field === key) {
        if (prev[0].direction === 'asc') {
          return [{ field: key, direction: 'desc' }, ...prev.slice(1)];
        }
        // Was desc → remove this column from sort (fall back to remaining or default)
        const remaining = prev.slice(1);
        return remaining.length > 0 ? remaining : [{ field: 'account_id', direction: 'asc' }];
      }
      // Different column: replace the first sort spec, keep remaining up to max 3
      return [{ field: key, direction: 'asc' }, ...prev.filter(s => s.field !== key).slice(0, 2)];
    });
  }, []);


  // ─── Grid Sort Info Helper ──────────────────────────────────────
  const getGridSortInfo = useCallback((key: string) => {
    const idx = gridSortSpecs.findIndex(s => s.field === key);
    if (idx < 0) return null;
    return { priority: idx + 1, direction: gridSortSpecs[idx].direction };
  }, [gridSortSpecs]);


  const toggleCol = useCallback((key: string) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleColDragStart = useCallback((key: string) => { dragCol.current = key; }, []);
  const handleColDrop = useCallback((targetKey: string) => {
    if (!dragCol.current || dragCol.current === targetKey) return;
    setColOrder(prev => {
      const next = [...prev];
      const fromIdx = next.indexOf(dragCol.current!);
      const toIdx = next.indexOf(targetKey);
      if (fromIdx < 0 || toIdx < 0) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, dragCol.current!);
      return next;
    });
    dragCol.current = null;
  }, []);

  const orderedVisibleCols = useMemo(() =>
    colOrder.filter(k => visibleCols.has(k)).map(k => ALL_COLUMNS.find(c => c.key === k)!).filter(Boolean),
    [colOrder, visibleCols]
  );

  // ─── Quick Filter Presets ──────────────────────────────────────
  const applyQuickFilter = useCallback((preset: string) => {

    switch (preset) {
      case 'this_month': {
        const r = getCurrentMonthRange();
        setDateFrom(r.from); setDateTo(r.to);
        break;
      }
      case 'last_month': {
        const r = getLastMonthRange();
        setDateFrom(r.from); setDateTo(r.to);
        break;
      }
      case 'all_series':
        setSeriesFilter(ACCOUNT_SERIES_KEYS); break;
      case 'billed_only':
        setBilledFilter('billed'); break;
      case 'non_billed':
        setBilledFilter('non-billed'); break;
      case 'validated_only':
        setValidatedOnly(true); break;
      case 'delivered_only':
        setStageFilter('Delivered'); break;
      case 'clear_all':
        setSeriesFilter(ACCOUNT_SERIES_KEYS);
        setAccountNumber(''); setAccountName('');
        setBilledFilter('all'); setValidatedOnly(false);
        setStageFilter(''); setProjectFilter('');
        setFleetFilter(''); setDriverFilter('');
        setVehicleFilter(''); setFilterAccountIds([]);
        setShowAccountPicker(false);
        setAccountFilterSeries(ACCOUNT_SERIES_KEYS);
        break;

      default:
        // "5_series", "9_series" etc -- one quick-filter per configured series
        if (preset.endsWith('_series') && ACCOUNT_SERIES_KEYS.includes(preset.split('_')[0])) {
          setSeriesFilter([preset.split('_')[0]]);
        }
    }
  }, []);


  // ─── Invoice Handlers (Part 6) ─────────────────────────────────
  const handleInvoiceAssigned = useCallback((
    assignments: Map<string, InvoiceAssignment>,
    summary: InvoiceRunSummary,
    settings: IInvoiceRunSettings
  ) => {
    setInvoiceAssignments(assignments);
    setInvoiceSummary(summary);
    setInvoiceSettings(settings);
    toast({
      title: 'Invoices Assigned',
      description: `${summary.uniqueGroups} invoice numbers assigned (${summary.startingNumber}-${summary.endingNumber}) across ${summary.totalRows} rows`,
    });
  }, []);

  const handleInvoiceCleared = useCallback(() => {
    setInvoiceAssignments(null);
    setInvoiceSummary(null);
    setInvoiceSettings(null);
    toast({ title: 'Invoices Cleared', description: 'Invoice number assignments have been cleared' });
  }, []);

  // ─── Anomaly Detection Handlers (Part 7) ───────────────────────
  const handleRunAnomalyDetection = useCallback(async () => {
    setAnomalyRunning(true);
    try {
      const { results, summary } = runAnomalyDetection(displayRows as any, anomalyConfig);
      const jobIds = Array.from(results.values()).map(r => r.jobId);
      if (jobIds.length > 0) {
        const { data: reviews, error: reviewErr } = await supabase
          .from('export_anomaly_reviews')
          .select('*')
          .in('job_id', jobIds);

        if (reviewErr) {
          console.error('Failed to load anomaly reviews:', reviewErr);
          toast({ title: 'Warning', description: 'Anomaly scan complete but could not load saved reviews. Review statuses may not be current.', variant: 'destructive' });
        }

        if (reviews && reviews.length > 0) {
          const merged = mergeReviewStatuses(results, reviews);
          setAnomalyResults(merged.results);
          setAnomalySummary(merged.summary);
        } else {
          setAnomalyResults(results);
          setAnomalySummary(summary);
        }
      } else {
        setAnomalyResults(results);
        setAnomalySummary(summary);
      }

      setShowAnomalyPanel(true);
      toast({
        title: 'Anomaly Scan Complete',
        description: `${summary.totalFlagged} rows flagged with ${summary.totalFlags} anomalies across ${displayRows.length} rows`,
      });
    } catch (err: any) {
      console.error('Anomaly detection error:', err);
      toast({ title: 'Anomaly Scan Failed', description: err.message || 'Failed to run anomaly detection. Try again or check the console for details.', variant: 'destructive' });
    } finally {
      setAnomalyRunning(false);
    }
  }, [displayRows, anomalyConfig]);

  const handleAnomalyResultsChange = useCallback((results: Map<string, RowAnomalyResult>, summary: AnomalySummary) => {
    setAnomalyResults(results);
    setAnomalySummary(summary);
  }, []);

  // ─── Export History: Load Settings from a Previous Run ──────────
  const handleLoadExportRunSettings = useCallback(async (run: ExportRun) => {
    // Restore template selection
    if (run.template_id) {
      setSelectedTemplateId(run.template_id);
      setTemplatePanelOpen(true);
    }
    // Restore filters if available
    if (run.filters_snapshot) {
      const f = run.filters_snapshot;
      if (f.dateFrom) setDateFrom(f.dateFrom);
      if (f.dateTo) setDateTo(f.dateTo);
      if (f.seriesFilter) setSeriesFilter(f.seriesFilter);
      if (f.accountNumber !== undefined) setAccountNumber(f.accountNumber);
      if (f.accountName !== undefined) setAccountName(f.accountName);
      if (f.billedFilter) setBilledFilter(f.billedFilter);
      if (f.validatedOnly !== undefined) setValidatedOnly(f.validatedOnly);
      if (f.stageFilter !== undefined) setStageFilter(f.stageFilter);
      if (f.projectFilter !== undefined) setProjectFilter(f.projectFilter);
      if (f.fleetFilter !== undefined) setFleetFilter(f.fleetFilter);
      if (f.driverFilter !== undefined) setDriverFilter(f.driverFilter);
      if (f.vehicleFilter !== undefined) setVehicleFilter(f.vehicleFilter);
      // Restore non-exported account filter
      if (f.filterAccountIds && Array.isArray(f.filterAccountIds)) {
        setFilterAccountIds(f.filterAccountIds);
        if (f.filterAccountIds.length > 0) setShowAccountPicker(true);
      }
      if (f.accountFilterSeries && Array.isArray(f.accountFilterSeries)) {
        setAccountFilterSeries(f.accountFilterSeries);
      }
    }


  }, []);


  // ─── Export Panel Open ─────────────────────────────────────────
  const handleOpenExportPanel = useCallback(async () => {
    if (!selectedTemplateId) return;
    try {
      const { data, error } = await supabase.from('export_templates').select('*').eq('id', selectedTemplateId).single();
      if (error) throw error;
      if (data) {
        setSelectedTemplate(data as ExportTemplate);
        setShowExportPanel(true);
      }
    } catch (err: any) {
      toast({ title: 'Error', description: 'Failed to load template for export. Try again.', variant: 'destructive' });
    }
  }, [selectedTemplateId]);

  // ─── Grid rows with optional flagged-only filter ───────────────
  const gridRows = useMemo(() => {
    if (!showFlaggedOnly || !anomalyResults) return displayRows;
    return displayRows.filter(r => anomalyResults.has(r.tc_id));
  }, [displayRows, showFlaggedOnly, anomalyResults]);

  const hasAnomalyData = anomalyResults && anomalyResults.size > 0;

  // ─── Cell renderer ─────────────────────────────────────────────
  const renderCell = useCallback((row: ExportJobRow, col: ColDef) => {
    const val = (row as any)[col.key];
    switch (col.key) {
      case 'grand_total': case 'direct_settled': case 'paid_amount': case 'cash_amt':
      case 'eftpos_amt': case 'totalmobility_amt': case 'tm_hoist_amt': case 'account_amt':
      case 'other_amt': case 'discount': case 'net_after_tm': case 'ecard_amt':
      case 'trip_total': case 'trip_base_fare':
        return <span className={Number(val || 0) === 0 ? 'text-muted-foreground/50' : ''}>{fmt(val)}</span>;
      case 'pu_time': case 'do_time':
        return fmtTime(val);
      case 'pu_date':
        if (!val) return '-';
        // Format YYYY-MM-DD to DD-MM-YY (NZ format)
        const parts = val.split('-');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0].slice(2)}`;
        }
        return val;

      case 'account_series':
        if (!val) return <span className="text-muted-foreground/40">-</span>;
        return <Badge className={`${seriesBadgeClass(val)} text-[10px] px-1.5 py-0`}>{val}</Badge>;
      case 'billed_status':
        if (!val) return <span className="text-muted-foreground/40">-</span>;
        return <Badge className={val === 'Billed' ? 'bg-green-900/40 text-green-400 border-green-700/50 text-[10px] px-1.5 py-0' : 'bg-secondary text-muted-foreground text-[10px] px-1.5 py-0'}>{val}</Badge>;
      case 'stage':
        if (!val) return '-';
        const stc: Record<string, string> = { 'Delivered': 'text-green-400', 'Cancelled': 'text-red-400', 'No show': 'text-orange-400' };
        return <span className={stc[val] || 'text-muted-foreground'}>{val}</span>;
      case 'validation_status': {
        const vs = getValidationStatus(row);
        if (vs === 'fully_validated') return <Badge className="bg-green-900/40 text-green-400 border-green-700/50 text-[10px] px-1.5 py-0 gap-1"><CheckCircle2 className="w-3 h-3" />Full</Badge>;
        if (vs === 'partially_validated') return <Badge className="bg-yellow-900/40 text-yellow-400 border-yellow-700/50 text-[10px] px-1.5 py-0 gap-1"><MinusCircle className="w-3 h-3" />Partial</Badge>;
        if (vs === 'no_payment') return <Badge className="bg-gray-800/40 text-gray-500 border-gray-700/50 text-[10px] px-1.5 py-0 gap-1"><XCircle className="w-3 h-3" />No Pay</Badge>;
        return <Badge className="bg-red-900/40 text-red-400 border-red-700/50 text-[10px] px-1.5 py-0 gap-1"><XCircle className="w-3 h-3" />None</Badge>;
      }
      case 'missing_data': {
        const warnings: string[] = [];
        if (row.missing_crm_account) warnings.push('CRM');
        if (row.missing_payment_row) warnings.push('Pay');
        if (row.missing_project) warnings.push('Proj');
        if (row.missing_reference) warnings.push('Ref');
        if (row.missing_cost_code) warnings.push('CC');
        if (warnings.length === 0) return <span className="text-green-500/60 text-[10px]">OK</span>;
        return (
          <div className="flex flex-wrap gap-0.5">
            {warnings.map(w => (
              <span key={w} className="text-[9px] px-1 py-0 rounded bg-amber-900/30 text-amber-400 border border-amber-700/30">{w}</span>
            ))}
          </div>
        );
      }
      default:
        if (val === null || val === undefined || val === '') return <span className="text-muted-foreground/40">-</span>;
        return <span className="truncate block max-w-full">{String(val)}</span>;
    }
  }, []);
  const toggleSeries = useCallback((s: string) => {
    setSeriesFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  }, []);


  const enabledColCount = useMemo(() => templateColumns.filter(c => c.is_enabled).length, [templateColumns]);
  // Per-column account filter indicator
  const perColumnAccountFilter = useMemo(() => {
    const col = templateColumns.find(
      c => c.source_type === 'source_field' && c.source_field === 'account_id'
        && c.transform_config?.selectedAccountIds?.length > 0
    );
    return col ? (col.transform_config.selectedAccountIds as string[]) : [];
  }, [templateColumns]);
  const canExport = selectedTemplateId && enabledColCount > 0 && displayRows.length > 0;


  return (
    <TooltipProvider delayDuration={200}>
    <div className="flex gap-0 items-stretch min-h-[calc(100vh-120px)]">

      <div className={`flex-1 min-w-0 space-y-0 transition-all duration-300 ${templatePanelOpen ? 'mr-0' : ''}`}>
        {/* Header */}
        <Card className="bg-card border-border rounded-b-none border-b-0">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-6 h-6 text-primary" />
                <div>
                  <CardTitle className="golden-text text-xl">Export Builder</CardTitle>
                  <p className="text-muted-foreground text-sm mt-0.5">
                    Joined job view — TaxiCaller + Payments + CRM — one row per job
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                {/* ─── Row 1: Primary Actions ─── */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant={templatePanelOpen ? 'default' : 'outline'}
                    onClick={() => setTemplatePanelOpen(!templatePanelOpen)}
                    className={templatePanelOpen ? 'golden-button' : 'border-border text-muted-foreground hover:text-foreground'}
                  >
                    {templatePanelOpen ? <PanelRightOpen className="w-4 h-4 mr-1" /> : <PanelRightOpen className="w-4 h-4 mr-1" />}
                    Templates
                  </Button>
                  {selectedTemplateId && enabledColCount > 0 && (
                    <Button
                      size="sm"
                      variant={showPreview ? 'default' : 'outline'}
                      onClick={() => setShowPreview(!showPreview)}
                      className={showPreview ? 'bg-cyan-700 hover:bg-cyan-600 text-white' : 'border-border text-muted-foreground hover:text-foreground'}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                  )}
                  {canExport && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleOpenExportPanel}
                      className="border-green-700/50 text-green-400 hover:bg-green-900/20 hover:text-green-300"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Export
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowExportHistory(true)}
                    className="border-border text-muted-foreground hover:text-foreground"
                  >
                    <History className="w-4 h-4 mr-1" />
                    History
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowInvoicedJobsReport(true)} className="border-indigo-700/50 text-indigo-400 hover:bg-indigo-900/20 hover:text-indigo-300">
                    <Receipt className="w-4 h-4 mr-1" />
                    Invoiced Jobs
                  </Button>
                </div>

                {/* ─── Row 2: Tools & Settings ─── */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setGridSortDialogOpen(true)}
                    className={`${gridSortSpecs.length > 1 ? 'border-blue-500/50 text-blue-400 hover:bg-blue-900/10' : 'border-border text-muted-foreground hover:text-foreground'}`}
                  >
                    <Database className="w-4 h-4 mr-1" />
                    Data Sort{gridSortSpecs.length > 0 ? ` (${gridSortSpecs.length})` : ''}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSortDialogOpen(true)}
                    disabled={!selectedTemplateId || enabledColCount === 0}
                    className={`${previewSortSpecs.length > 0 ? 'border-primary/50 text-primary hover:bg-primary/10' : 'border-border text-muted-foreground hover:text-foreground'} ${!selectedTemplateId || enabledColCount === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <ArrowUpDown className="w-4 h-4 mr-1" />
                    Sort{previewSortSpecs.length > 0 ? ` (${previewSortSpecs.length})` : ''}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setAliasManagerOpen(true)} className="border-border text-muted-foreground hover:text-foreground">
                    <MapPin className="w-4 h-4 mr-1" />
                    Alias Rules
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setTownClassifierOpen(true)} className="border-emerald-700/50 text-emerald-400 hover:bg-emerald-900/20 hover:text-emerald-300">
                    <Globe className="w-4 h-4 mr-1" />
                    Towns
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setFiltersExpanded(!filtersExpanded)} className="border-border text-muted-foreground hover:text-foreground">
                    <Filter className="w-4 h-4 mr-1" />
                    Filters
                    <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${filtersExpanded ? 'rotate-180' : ''}`} />
                  </Button>
                  <Popover open={colPickerOpen} onOpenChange={setColPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button size="sm" variant="outline" className="border-border text-muted-foreground hover:text-foreground">
                        <Columns3 className="w-4 h-4 mr-1" />
                        Columns ({visibleCols.size})
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 max-h-[400px] overflow-auto p-3" align="end">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Show / Hide Columns</p>
                      {['TaxiCaller', 'Payment', 'CRM', 'Derived'].map(group => (
                        <div key={group} className="mb-3">
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">{group}</p>
                          {ALL_COLUMNS.filter(c => c.group === group).map(c => (
                            <label key={c.key} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-secondary/40 px-1 rounded text-sm">
                              <Checkbox checked={visibleCols.has(c.key)} onCheckedChange={() => toggleCol(c.key)} />
                              {c.label}
                            </label>
                          ))}
                        </div>
                      ))}
                    </PopoverContent>
                  </Popover>

                  <Button size="sm" variant="outline" onClick={() => loadData()} className="border-border text-muted-foreground hover:text-foreground">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

            </div>

            {/* ─── Workflow Step Indicators ─── */}
            <div className="flex items-center gap-1 mt-3 pt-2 border-t border-border/30">
              {workflowSteps.map((step, idx) => (
                <React.Fragment key={step.label}>
                  {idx > 0 && <div className="w-4 h-px bg-border/40" />}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors cursor-default ${
                        step.done
                          ? 'bg-green-900/30 text-green-400 border border-green-700/30'
                          : step.active
                            ? 'bg-primary/10 text-primary border border-primary/30'
                            : 'bg-secondary/30 text-muted-foreground/50 border border-border/20'
                      }`}>
                        {step.done ? (
                          <CheckCircle2 className="w-2.5 h-2.5" />
                        ) : step.active ? (
                          <Circle className="w-2.5 h-2.5" />
                        ) : (
                          <Circle className="w-2.5 h-2.5 opacity-30" />
                        )}
                        {step.label}
                        {step.optional && <span className="opacity-40 ml-0.5">?</span>}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-card border-border text-xs">
                      {step.done ? `${step.label} complete` : step.active ? `${step.label} — next step` : `${step.label} — waiting`}
                    </TooltipContent>
                  </Tooltip>
                </React.Fragment>
              ))}
              <div className="flex-1" />
              {/* Readiness indicator */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${
                    readiness.state === 'ready'
                      ? 'bg-green-900/30 text-green-400 border-green-700/40'
                      : readiness.state === 'needs_review'
                        ? 'bg-amber-900/30 text-amber-400 border-amber-700/40'
                        : 'bg-gray-800/40 text-gray-500 border-gray-700/40'
                  }`}>
                    {readiness.state === 'ready' ? (
                      <CheckCircle2 className="w-3 h-3" />
                    ) : readiness.state === 'needs_review' ? (
                      <AlertTriangle className="w-3 h-3" />
                    ) : (
                      <XCircle className="w-3 h-3" />
                    )}
                    {readiness.label}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-card border-border text-xs max-w-[280px]">
                  {readiness.reasons.length > 0 ? (
                    <ul className="space-y-0.5">
                      {readiness.reasons.map((r, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-amber-400 mt-0.5">•</span>
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-green-400">All checks passed. Ready to export.</p>
                  )}
                </TooltipContent>
              </Tooltip>
            </div>
          </CardHeader>

          {/* Filters */}
          {filtersExpanded && (
            <CardContent className="pt-0 pb-4 border-t border-border/50">
              {/* Quick Filter Chips */}
              <div className="flex flex-wrap items-center gap-1.5 mb-3 mt-2">
                <Zap className="w-3 h-3 text-muted-foreground/60" />
                <span className="text-[10px] text-muted-foreground/60 mr-1">Quick:</span>
                {[
                  { key: 'this_month', label: 'This Month', icon: CalendarDays },
                  { key: 'last_month', label: 'Last Month', icon: Clock },
                  ...ACCOUNT_SERIES.map(s => ({ key: `${s.key}_series`, label: `${s.key}-Series` })),
                  { key: 'all_series', label: 'All Series' },
                  { key: 'billed_only', label: 'Billed Only' },
                  { key: 'non_billed', label: 'Non-Billed' },
                  { key: 'validated_only', label: 'Validated Only' },
                  { key: 'delivered_only', label: 'Delivered Only' },
                  { key: 'clear_all', label: 'Clear All', icon: XCircle },
                ].map(preset => (
                  <button
                    key={preset.key}
                    onClick={() => applyQuickFilter(preset.key)}
                    className="text-[10px] px-2 py-0.5 rounded-full border border-border/40 text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Date From</Label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="dark-input text-xs h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Date To</Label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="dark-input text-xs h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Account Series</Label>
                  <div className="flex gap-1 mt-1">
                    {ACCOUNT_SERIES_KEYS.map(s => (
                      <button key={s} onClick={() => toggleSeries(s)}
                        className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${seriesFilter.includes(s) ? 'bg-primary/20 text-primary border-primary/50' : 'bg-secondary/40 text-muted-foreground border-border hover:border-primary/30'}`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Account #</Label>
                  <Input value={accountNumber} onChange={e => setAccountNumber(e.target.value)} placeholder="e.g. 5001" className="dark-input text-xs h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Account Name</Label>
                  <Input value={accountName} onChange={e => setAccountName(e.target.value)} placeholder="Search..." className="dark-input text-xs h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Billed</Label>
                  <Select value={billedFilter} onValueChange={(v: any) => setBilledFilter(v)}>
                    <SelectTrigger className="dark-input text-xs h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="billed">Billed Only</SelectItem>
                      <SelectItem value="non-billed">Non-Billed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Validated Only</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Switch checked={validatedOnly} onCheckedChange={v => setValidatedOnly(v)} />
                    <span className="text-xs text-muted-foreground">{validatedOnly ? 'On' : 'Off'}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Stage</Label>
                  <Select value={stageFilter || '__all'} onValueChange={(v) => setStageFilter(v === '__all' ? '' : v)}>
                    <SelectTrigger className="dark-input text-xs h-8"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all">All Stages</SelectItem>
                      <SelectItem value="Delivered">Delivered</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                      <SelectItem value="No show">No Show</SelectItem>
                      <SelectItem value="Passenger on board">On Board</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Project</Label>
                  <Input value={projectFilter} onChange={e => setProjectFilter(e.target.value)} placeholder="Search..." className="dark-input text-xs h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Fleet</Label>
                  <Select value={fleetFilter || '__all'} onValueChange={(v) => setFleetFilter(v === '__all' ? '' : v)}>
                    <SelectTrigger className="dark-input text-xs h-8"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all">All Fleets</SelectItem>
                      <SelectItem value="tg&b">TG&B</SelectItem>
                      <SelectItem value="stss">STSS</SelectItem>
                      <SelectItem value="435">435</SelectItem>
                      <SelectItem value="458">458</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Driver</Label>
                  <Input value={driverFilter} onChange={e => setDriverFilter(e.target.value)} placeholder="Search..." className="dark-input text-xs h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase">Vehicle</Label>
                  <Input value={vehicleFilter} onChange={e => setVehicleFilter(e.target.value)} placeholder="e.g. 416" className="dark-input text-xs h-8" />
                </div>
              </div>


            </CardContent>
          )}
        </Card>



        {/* ─── Sticky Context / Summary Bar ─── */}
        <div className="bg-card border border-border border-t-0 border-b-0 px-4 py-2 flex flex-wrap items-center gap-4 text-xs sticky top-0 z-20">
          <div className="flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">Rows:</span>
            <span className="font-semibold text-foreground">{totalCount.toLocaleString()}</span>
            {loading && loadingProgress && (
              <span className="text-blue-400 text-[10px] ml-1">
                <Loader2 className="w-3 h-3 inline animate-spin mr-0.5" />
                {loadingProgress}
              </span>
            )}
            {(validatedOnly || (accountScope?.mode === 'selected' && accountScope.selectedAccountIds.length > 0)) && (
              <span className="text-muted-foreground">(showing {stats.count}{accountScope?.mode === 'selected' ? ` · ${accountScope.selectedAccountIds.length} accts` : ''})</span>
            )}
          </div>
          {rows.length > 0 && rows.length === totalCount && (
            <Badge className="bg-green-900/30 text-green-400 border-green-700/40 text-[9px] px-1.5 py-0">All loaded</Badge>
          )}
          {filterAccountIds.length > 0 && (
            <>
              <div className="h-4 w-px bg-border" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-default">
                    <EyeOff className="w-3 h-3 text-teal-400" />
                    <span className="text-teal-400 font-semibold">{filterAccountIds.length} acct filter</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-card border-border text-xs max-w-[280px]">
                  <p className="text-teal-400 font-medium mb-1">Non-Exported Account Filter Active</p>
                  <p className="text-muted-foreground">Showing only jobs for {filterAccountIds.length} selected account{filterAccountIds.length !== 1 ? 's' : ''}. This filter does not add columns to the export.</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
          {perColumnAccountFilter.length > 0 && (
            <>
              <div className="h-4 w-px bg-border" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-default">
                    <Users className="w-3 h-3 text-teal-400" />
                    <span className="text-teal-400 font-semibold">{perColumnAccountFilter.length} col acct filter</span>
                    <span className="text-teal-400/50 text-[9px]">(preview/export only)</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-card border-border text-xs max-w-[300px]">
                  <p className="text-teal-400 font-medium mb-1">Column Account Filter — Preview & Export Only</p>
                  <p className="text-muted-foreground">The Account Code column has {perColumnAccountFilter.length} account{perColumnAccountFilter.length !== 1 ? 's' : ''} selected: {perColumnAccountFilter.slice(0, 5).join(', ')}{perColumnAccountFilter.length > 5 ? '...' : ''}</p>
                  <p className="text-muted-foreground/70 mt-1">This filter does <strong>not</strong> affect the main data grid above. It only filters rows in the Template Output Preview and the final Export file.</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}




          <div><span className="text-muted-foreground">Grand Total:</span> <span className="font-semibold text-foreground">${stats.gt.toFixed(2)}</span></div>
          <div><span className="text-muted-foreground">TM:</span> <span className="font-semibold text-blue-400">${stats.tm.toFixed(2)}</span></div>
          <div><span className="text-muted-foreground">Net:</span> <span className="font-semibold text-green-400">${stats.net.toFixed(2)}</span></div>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-400" /><span className="text-green-400">{stats.fv}</span></div>
          <div className="flex items-center gap-1"><MinusCircle className="w-3 h-3 text-yellow-400" /><span className="text-yellow-400">{stats.pv}</span></div>
          <div className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-400" /><span className="text-red-400">{stats.nv}</span></div>
          {selectedTemplateId && (
            <>
              <div className="h-4 w-px bg-border" />
              <div className="flex items-center gap-1">
                <ListChecks className="w-3 h-3 text-primary" />
                <span className="text-primary font-medium">{enabledColCount} cols</span>
              </div>
            </>
          )}
          {invoiceSummary && (
            <>
              <div className="h-4 w-px bg-border" />
              <div className="flex items-center gap-1">
                <Hash className="w-3 h-3 text-indigo-400" />
                <span className="text-indigo-400 font-semibold">{invoiceSummary.uniqueGroups} invoices</span>
                <span className="text-muted-foreground">({invoiceSummary.startingNumber}-{invoiceSummary.endingNumber})</span>
              </div>
            </>
          )}
          {!invoiceSummary && previousInvoices.size > 0 && (
            <>
              <div className="h-4 w-px bg-border" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-default">
                    <Receipt className="w-3 h-3 text-indigo-400/60" />
                    <span className="text-indigo-400/60 font-medium">{previousInvoices.size} previously invoiced</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-card border-border text-xs max-w-[280px]">
                  <p className="text-indigo-400 font-medium mb-1">Previously Invoiced Jobs</p>
                  <p className="text-muted-foreground">{previousInvoices.size} job{previousInvoices.size !== 1 ? 's' : ''} in the current dataset {previousInvoices.size !== 1 ? 'have' : 'has'} been exported with invoice numbers in a previous run.</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
          {anomalySummary && (
            <>
              <div className="h-4 w-px bg-border" />
              <button
                onClick={() => setShowAnomalyPanel(!showAnomalyPanel)}
                className="flex items-center gap-1 hover:opacity-80 transition-opacity"
              >
                <AlertTriangle className="w-3 h-3 text-amber-400" />
                <span className="text-amber-400 font-semibold">{anomalySummary.totalFlagged} flagged</span>
                <span className="text-muted-foreground">({anomalySummary.unreviewed} pending)</span>
              </button>
              {hasAnomalyData && (
                <button
                  onClick={() => setShowFlaggedOnly(!showFlaggedOnly)}
                  className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${showFlaggedOnly ? 'bg-amber-900/30 text-amber-400 border-amber-700/40' : 'border-border/40 text-muted-foreground hover:border-border'}`}
                >
                  {showFlaggedOnly ? 'Show All' : 'Flagged Only'}
                </button>
              )}
            </>
          )}

        </div>

        {/* Grid */}
        <Card className="bg-card border-border rounded-t-none">
          <CardContent className="p-0">
            {loading && rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mb-2" />
                <p className="text-sm">Loading all jobs{loadingProgress ? '...' : '...'}</p>
                {loadingProgress && <p className="text-xs mt-1 text-blue-400">{loadingProgress}</p>}
              </div>
            ) : gridRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                <Search className="w-10 h-10 mb-3 opacity-40" />
                {showFlaggedOnly ? (
                  <>
                    <p className="text-sm">No flagged rows to show.</p>
                    <p className="text-xs mt-1 opacity-60">All rows in the current dataset are clean or have been reviewed.</p>
                    <Button size="sm" variant="ghost" onClick={() => setShowFlaggedOnly(false)} className="mt-2 text-xs">Show All Rows</Button>
                  </>
                ) : rows.length === 0 ? (
                  <>
                    <p className="text-sm">No jobs found for the current filters.</p>
                    <p className="text-xs mt-1 opacity-60 max-w-md text-center">
                      Try widening the date range, clearing account filters, or selecting more account series.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => applyQuickFilter('this_month')} className="text-xs border-border">
                        <CalendarDays className="w-3 h-3 mr-1" /> This Month
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => applyQuickFilter('clear_all')} className="text-xs border-border">
                        <XCircle className="w-3 h-3 mr-1" /> Clear All Filters
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm">No rows match the "Validated Only" filter.</p>
                    <p className="text-xs mt-1 opacity-60">Turn off "Validated Only" to see all rows.</p>
                    <Button size="sm" variant="ghost" onClick={() => setValidatedOnly(false)} className="mt-2 text-xs">Show All Rows</Button>
                  </>
                )}
              </div>
            ) : (
              <div className="overflow-auto max-h-[calc(100vh-440px)] min-h-[300px]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-10 bg-card">
                    <tr className="border-b border-border">
                      {hasAnomalyData && (
                        <th className="px-1 py-2 text-[10px] font-medium text-amber-400 whitespace-nowrap bg-card border-b border-border w-8 text-center">
                          <AlertTriangle className="w-3 h-3 mx-auto" />
                        </th>
                      )}
                      {((invoiceAssignments && invoiceAssignments.size > 0) || previousInvoices.size > 0) && (
                        <th className="px-2 py-2 text-[10px] font-medium text-indigo-400 whitespace-nowrap bg-card border-b border-border w-16 text-center">
                          <div className="flex items-center gap-0.5 justify-center">
                            <Hash className="w-3 h-3" />Inv #
                          </div>
                        </th>
                      )}

                      <th className="px-1 py-2 text-[10px] font-medium text-muted-foreground/60 whitespace-nowrap bg-card border-b border-border w-8 text-center" title="Edit Job">
                        <Pencil className="w-3 h-3 mx-auto" />
                      </th>

                      {orderedVisibleCols.map(col => (
                        <th
                          key={col.key}
                          className="px-2 py-2 text-[10px] font-medium text-muted-foreground whitespace-nowrap select-none cursor-pointer hover:text-foreground transition-colors bg-card border-b border-border"
                          style={{ minWidth: col.width, textAlign: col.align || 'left' }}
                          onClick={() => handleSort(col.key)}
                          draggable
                          onDragStart={() => handleColDragStart(col.key)}
                          onDragOver={e => e.preventDefault()}
                          onDrop={() => handleColDrop(col.key)}
                        >
                          <span className="flex items-center gap-1" style={{ justifyContent: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start' }}>
                            {col.label}
                            {(() => {
                              const si = getGridSortInfo(col.key);
                              if (!si) return null;
                              return (
                                <span className="flex items-center gap-0 flex-shrink-0">
                                  {si.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-400" /> : <ArrowDown className="w-3 h-3 text-blue-400" />}
                                  {gridSortSpecs.length > 1 && <span className="text-[8px] text-blue-400 font-bold -ml-0.5">*{si.priority}</span>}
                                </span>
                              );
                            })()}
                          </span>

                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {gridRows.map((row, idx) => {
                      const invAssignment = invoiceAssignments?.get(row.tc_id);
                      const rowAnomaly = anomalyResults?.get(row.tc_id);
                      const hasFlags = rowAnomaly && rowAnomaly.flags.length > 0;
                      return (
                        <tr key={row.tc_id || idx} className={`border-b border-border/50 hover:bg-secondary/30 transition-colors ${hasFlags && rowAnomaly.reviewStatus === 'flagged' ? 'bg-amber-900/5' : ''}`}>
                          {hasAnomalyData && (
                            <td className="px-1 py-1.5 text-center">
                              {hasFlags ? (
                                <div className="flex gap-0.5 justify-center">
                                  {rowAnomaly.reviewStatus === 'reviewed' ? (
                                    <CheckCircle2 className="w-3 h-3 text-green-400/60" />
                                  ) : rowAnomaly.reviewStatus === 'ignored' ? (
                                    <span className="text-gray-500/40 text-[9px]">-</span>
                                  ) : (
                                    <AlertTriangle className="w-3 h-3 text-amber-400" />
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground/10 text-[9px]">-</span>
                              )}
                            </td>
                          )}
                          {((invoiceAssignments && invoiceAssignments.size > 0) || previousInvoices.size > 0) && (
                            <td className="px-2 py-1.5 text-center">
                              {invAssignment ? (
                                <Badge className="bg-indigo-900/30 text-indigo-400 border-indigo-700/40 text-[9px] px-1.5 py-0 font-mono">
                                  {invAssignment.assignedInvoiceNumber}
                                </Badge>
                              ) : previousInvoices.has(row.tc_id) ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge className="bg-gray-800/40 text-gray-400 border-gray-700/40 text-[9px] px-1.5 py-0 font-mono cursor-default opacity-70">
                                      {previousInvoices.get(row.tc_id)!.invoice_number}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="right" className="bg-card border-border text-xs max-w-[240px]">
                                    <p className="text-indigo-400 font-medium mb-1">Previously Invoiced</p>
                                    <div className="space-y-0.5 text-muted-foreground">
                                      <p>Invoice #: <span className="text-foreground font-mono">{previousInvoices.get(row.tc_id)!.invoice_number}</span></p>
                                      {previousInvoices.get(row.tc_id)!.template_name && (
                                        <p>Template: <span className="text-foreground">{previousInvoices.get(row.tc_id)!.template_name}</span></p>
                                      )}
                                      <p>Invoiced: <span className="text-foreground">{formatDateTime(previousInvoices.get(row.tc_id)!.invoiced_at)}</span></p>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-muted-foreground/20">-</span>
                              )}
                            </td>
                          )}

                          <td className="px-1 py-1.5 text-center">
                            <button
                              onClick={() => { setEditingRow(row); setEditModalOpen(true); }}
                              className="p-1 rounded hover:bg-primary/10 text-muted-foreground/40 hover:text-primary transition-colors"
                              title={`Edit Job ${row.job_id}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          </td>

                          {orderedVisibleCols.map(col => (
                            <td
                              key={col.key}
                              className="px-2 py-1.5 whitespace-nowrap"
                              style={{ textAlign: col.align || 'left', maxWidth: col.width ? col.width + 40 : 250 }}
                            >
                              {renderCell(row, col)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Row Count Footer */}
            {gridRows.length > 0 && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-border text-xs text-muted-foreground">
                <span>
                  Showing all {gridRows.length.toLocaleString()} rows
                  {gridRows.length !== totalCount && ` (${totalCount.toLocaleString()} total)`}
                  {loading && ' — loading...'}
                </span>
                <span className="text-[10px] text-muted-foreground/50">Scroll to view all rows</span>
              </div>
            )}
          </CardContent>
        </Card>


        {/* Template Output Preview — uses exportFilteredRows (per-column account filter applied) */}
        {showPreview && selectedTemplateId && enabledColCount > 0 && displayRows.length > 0 && (
          <div className="mt-3">
            <OutputPreview
              columns={templateColumns}
              rows={exportFilteredRows as any}
              aliasRules={aliasRules}
              marginRules={marginRules}
              templateName={undefined}
              invoiceAssignments={previewInvoiceAssignments}
              invoiceSummary={previewInvoiceSummary}
              townClassifications={townClassificationsMap}
              rangeEndDate={dateTo}
              sortSpecs={previewSortSpecs}
              onSortSpecsChange={setPreviewSortSpecs}
            />



          </div>
        )}



        {/* Anomaly Panel */}
        {showAnomalyPanel && (
          <div className="mt-3">
            <AnomalyPanel
              rows={displayRows as any}
              anomalyResults={anomalyResults}
              anomalySummary={anomalySummary}
              anomalyConfig={anomalyConfig}
              onConfigChange={setAnomalyConfig}
              onRunDetection={handleRunAnomalyDetection}
              onResultsChange={handleAnomalyResultsChange}
              isRunning={anomalyRunning}
            />
          </div>
        )}
      </div>

      {/* Template Builder Panel + Invoice Run Settings + Anomaly */}
      {templatePanelOpen && (
        <div className="w-[380px] flex-shrink-0 ml-3 space-y-3 overflow-y-auto sticky top-0 self-start max-h-screen pb-6">
          <TemplateBuilder
            selectedTemplateId={selectedTemplateId}
            onTemplateChange={setSelectedTemplateId}
            onColumnsChange={setTemplateColumns}
            onMarginRulesChange={setMarginRules}
            accountScope={accountScope}
            onAccountScopeChange={setAccountScope}
            seriesFilter={seriesFilter}
            rangeEndDate={dateTo}
          />


          {selectedTemplateId && (
            <InvoiceRunSettings
              rows={displayRows as any}
              onInvoiceAssigned={handleInvoiceAssigned}
              onInvoiceCleared={handleInvoiceCleared}
              currentAssignments={invoiceAssignments}
              currentSummary={invoiceSummary}
              totalRowCount={totalCount}
              hasAccountScope={!!(accountScope?.mode === 'selected' && accountScope.selectedAccountIds.length > 0)}
              accountScopeCount={accountScope?.selectedAccountIds?.length || 0}
              hasColumnAccountFilter={perColumnAccountFilter.length > 0}
              columnAccountFilterCount={perColumnAccountFilter.length}
            />
          )}


          {!showAnomalyPanel && (
            <AnomalyPanel
              rows={displayRows as any}
              anomalyResults={anomalyResults}
              anomalySummary={anomalySummary}
              anomalyConfig={anomalyConfig}
              onConfigChange={setAnomalyConfig}
              onRunDetection={handleRunAnomalyDetection}
              onResultsChange={handleAnomalyResultsChange}
              isRunning={anomalyRunning}
            />
          )}
        </div>
      )}

      {/* Address Alias Manager Modal */}
      <AddressAliasManager
        open={aliasManagerOpen}
        onClose={() => setAliasManagerOpen(false)}
        onRulesChanged={loadAliasRules}
      />

      {/* Export Panel Modal — uses exportFilteredRows (per-column account filter applied) */}
      <ExportPanel
        open={showExportPanel}
        onClose={() => setShowExportPanel(false)}
        rows={exportFilteredRows as any}
        columns={templateColumns}
        aliasRules={aliasRules}
        marginRules={marginRules}
        invoiceAssignments={invoiceAssignments}
        invoiceSummary={invoiceSummary}
        anomalyResults={anomalyResults}
        anomalySummary={anomalySummary}
        template={selectedTemplate}
        invoiceSettings={invoiceSettings}
        anomalyConfig={anomalyConfig}
        filtersSnapshot={filtersSnapshot}
        townClassifications={townClassificationsMap}
        totalRowCount={totalCount}
        onExportComplete={() => {
          // Refresh previous invoices after a successful export so the grid shows them immediately
          loadPreviousInvoices(rows);
        }}
      />





      {/* Export History Modal */}
      <ExportHistory
        open={showExportHistory}
        onClose={() => setShowExportHistory(false)}
        onLoadSettings={handleLoadExportRunSettings}
      />
      {/* Invoiced Jobs Report Modal */}
      <InvoicedJobsReport
        open={showInvoicedJobsReport}
        onClose={() => setShowInvoicedJobsReport(false)}
      />

      {/* Town Classifier Modal */}
      <TownClassifier
        open={townClassifierOpen}
        onClose={() => setTownClassifierOpen(false)}
        rows={displayRows as any}
        classifications={townClassifications}
        onClassificationsChanged={loadTownClassifications}
      />

      {/* Job Edit Modal */}
      <JobEditModal
        open={editModalOpen}
        onClose={() => { setEditModalOpen(false); setEditingRow(null); }}
        row={editingRow as any}
        onSaved={(updatedRow) => {
          // Update the row in local state so the grid reflects the change immediately
          setRows(prev => prev.map(r => r.tc_id === updatedRow.tc_id ? { ...r, ...updatedRow } : r));
        }}
      />

      {/* Sort Dialog (Excel-like) */}
      <SortDialog
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortSpecs={previewSortSpecs}
        onApply={(specs) => setPreviewSortSpecs(specs)}
        columns={templateColumns}
        templateId={selectedTemplateId}
        onSaveToTemplate={selectedTemplateId ? handleSaveSortToTemplate : undefined}
        savedSort={templateDefaultSort}
      />

      {/* Grid Sort Dialog (multi-column server-side sort) */}
      <GridSortDialog
        open={gridSortDialogOpen}
        onClose={() => setGridSortDialogOpen(false)}
        sortSpecs={gridSortSpecs}
        onApply={(specs) => { setGridSortSpecs(specs.length > 0 ? specs : [{ field: 'account_id', direction: 'asc' }]); setPage(0); }}
        columns={ALL_COLUMNS}
        maxLevels={3}
      />


    </div>

    </TooltipProvider>
  );
};


export default ExportBuilder;
